import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { authenticatedFetch } from "../../../../utils/authenticatedFetch";
import { formatTimeAgo } from "../../../../utils/helpers";
import { CurrentUser } from "../../../../types/types";
import PostSkeleton from "../../PostSkeleton";

interface Comment {
  id: number;
  user_id: number;
  parent_id: number;
  body: string;
  views: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  comments_count: number;
  likes_count: number;
  user?: {
    id: number;
    name: string;
  };
}

interface CommentPaginatedResponse {
  current_page: number;
  last_page: number;
  data: Comment[];
  total?: number;
  per_page?: number;
}

interface CommentListProps {
  postId: number;
  currentUser: CurrentUser;
}

const CommentList: React.FC<CommentListProps> = ({ postId, currentUser }) => {
  const queryClient = useQueryClient();
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery<CommentPaginatedResponse>({
    queryKey: ["comments", postId],
    queryFn: ({ pageParam = 1 }) =>
      authenticatedFetch<CommentPaginatedResponse>(
        `/posts/${postId}/comments?page=${pageParam}`
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.current_page < lastPage.last_page
        ? lastPage.current_page + 1
        : undefined,
    enabled: !!postId,
  });

  const optimisticComments =
    queryClient.getQueryData<Comment[]>(["optimistic-comments", postId]) ?? [];

  const serverComments = data?.pages.flatMap((page) => page.data) ?? [];

  const allComments = [...serverComments, ...optimisticComments];

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [allComments.length]);

  if (isLoading) return <PostSkeleton count={3} />;
  if (isError)
    return (
      <div className="text-center py-6 text-red-600 text-sm">
        Failed to load comments
      </div>
    );

  if (allComments.length === 0)
    return (
      <div className="text-center py-6 text-gray-500 text-sm italic">
        No comments yet. Be the first to comment!
      </div>
    );

  return (
    <div
      ref={containerRef}
      className="space-y-5 max-h-[420px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-orange-300 scrollbar-track-gray-100"
    >
      {allComments.map((comment) => {
        const isOwn = comment.user_id === currentUser.id;
        const author = comment.user?.name || `User ${comment.user_id}`;

        return (
          <div key={comment.id} className="flex gap-3">
            <div className="flex-shrink-0 mt-1">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-300 to-pink-400 flex items-center justify-center text-white text-sm font-bold shadow-sm">
                {author.charAt(0).toUpperCase() || "?"}
              </div>
            </div>
            <div className="flex-1">
              <div className="bg-gray-50 rounded-2xl px-4 py-2.5 shadow-sm">
                <p className="font-medium text-sm text-gray-900">{author}</p>
                <p className="text-gray-800 text-sm whitespace-pre-wrap leading-relaxed mt-0.5">
                  {comment.body}
                </p>
              </div>
              <div className="mt-1.5 flex items-center gap-3.5 text-xs text-gray-500">
                <span>{formatTimeAgo(comment.created_at)}</span>
                {isOwn && (
                  <button className="text-red-600 hover:text-red-800 transition-colors font-medium text-xs">
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {isFetchingNextPage && <PostSkeleton count={2} />}

      {hasNextPage && !isFetchingNextPage && (
        <div className="py-4 text-center">
          <button
            onClick={() => fetchNextPage()}
            className="text-sm text-orange-600 hover:text-orange-800 font-medium transition"
          >
            Load more comments →
          </button>
        </div>
      )}
    </div>
  );
};

export default CommentList;
